import React from 'react';
import { Tooltip as MuiTooltip, styled, tooltipClasses } from '@mui/material';
import type { TooltipProps as MuiTooltipProps } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Colors } from 'library';
import { Icon } from './Icon';
import { Stack } from './Stack';
import { Text } from './Text';

interface TooltipProps {
    body: React.ReactNode;
    children?: React.ReactNode;
    icon?: SvgIconComponent | IconDefinition;
    heading?: React.ReactNode;
    placement?: MuiTooltipProps['placement'];
    arrow?: boolean;
    className?: string;
}

export function Tooltip({ icon, children, body, heading, placement = 'top', arrow = true, className }: TooltipProps) {
    return (
        <StyledTooltip
            placement={placement}
            arrow={arrow}
            className={className}
            title={
                <Stack gap="sm">
                    {heading && (
                        <Text weight="semibold" tone="overlay">
                            {heading}
                        </Text>
                    )}
                    <div>{body}</div>
                </Stack>
            }
        >
            <Trigger>
                {children ?? (icon && <Icon icon={icon} size="sm" bgColor="transparent" iconColor={Colors.mediumGrey} />)}
            </Trigger>
        </StyledTooltip>
    );
}

const Trigger = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'help',
    lineHeight: 0,
});

const StyledTooltip = styled(({ className, ...props }: MuiTooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: Colors.primary,
        color: Colors.white,
        fontSize: 12,
        lineHeight: 1.5,
        padding: '8px 10px',
        borderRadius: 8,
        maxWidth: 260,
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: Colors.primary,
    },
});
